import { useEffect, useState } from "react";
import { Link } from "react-router-dom";
import { Calendar, ArrowRight } from "lucide-react";
import { api } from "@/lib/api";

function addDaysIso(iso, n) {
  const d = new Date(iso);
  d.setDate(d.getDate() + n);
  return d.toISOString().slice(0, 10);
}

const WEEKDAY_SHORT = ["søn", "man", "tir", "ons", "tor", "fre", "lør"];

function relativeDay(dateStr) {
  const today = new Date().toISOString().slice(0, 10);
  if (dateStr === today) return "I dag";
  if (dateStr === addDaysIso(today, 1)) return "I morgen";
  const d = new Date(dateStr);
  return `${WEEKDAY_SHORT[d.getDay()]} ${String(d.getDate()).padStart(2, "0")}.${String(d.getMonth() + 1).padStart(2, "0")}`;
}

export default function UpcomingCalendarCard({ size = "medium" }) {
  const [events, setEvents] = useState(null);

  useEffect(() => {
    const today = new Date().toISOString().slice(0, 10);
    const end = addDaysIso(today, 7);
    api.listCalendarEvents(today, end)
      .then((res) => setEvents(res.data?.events || []))
      .catch(() => setEvents([]));
  }, []);

  if (events === null) {
    return (
      <div className="bg-white border border-stone-100 rounded-xl p-4 animate-pulse" data-testid="home-calendar-loading">
        <div className="h-4 w-32 bg-stone-100 rounded mb-3" />
        <div className="space-y-2">
          <div className="h-8 bg-stone-50 rounded" />
          <div className="h-8 bg-stone-50 rounded" />
        </div>
      </div>
    );
  }

  const limit = size === "large" ? 8 : size === "small" ? 3 : 5;
  const shown = events.slice(0, limit);

  return (
    <Link
      to="/calendar"
      className="block bg-white border border-stone-100 hover:border-amber-300 rounded-xl p-4 transition-colors"
      data-testid="home-calendar"
    >
      <div className="flex items-center justify-between mb-3">
        <h3 className="text-sm font-semibold text-stone-800 flex items-center gap-2" style={{ fontFamily: "'Cormorant Garamond', serif" }}>
          <Calendar className="h-4 w-4 text-amber-600" />
          <span className="text-base">Kommende avtaler</span>
        </h3>
        <ArrowRight className="h-4 w-4 text-stone-300" />
      </div>
      {shown.length === 0 ? (
        <p className="text-xs text-stone-400 italic py-2">Ingen avtaler neste uke. Trykk for å legge til.</p>
      ) : (
        <ul className="space-y-1.5">
          {shown.map((ev) => (
            <li key={ev.id} className="flex items-baseline gap-3" data-testid={`home-cal-ev-${ev.id}`}>
              <span
                className="text-[10px] uppercase tracking-wider font-bold text-stone-400 w-14 flex-shrink-0"
                title={ev.date}
              >
                {relativeDay(ev.date)}
              </span>
              {ev.time && (
                <span className="text-[11px] font-mono text-stone-500 flex-shrink-0">{ev.time}</span>
              )}
              <span className="text-sm text-stone-800 truncate flex-1">{ev.title}</span>
              {ev.assignee_name && (
                <span
                  className="hidden sm:inline text-[10px] font-semibold px-1.5 py-0.5 rounded-full text-white flex-shrink-0"
                  style={{ backgroundColor: ev.assignee_color || "#a8a29e" }}
                >
                  {ev.assignee_name}
                </span>
              )}
            </li>
          ))}
        </ul>
      )}
      {events.length > limit && (
        <p className="text-[10px] text-stone-400 mt-2">+ {events.length - limit} flere avtaler</p>
      )}
    </Link>
  );
}
